import React from 'react';
import {Link} from 'react-router-dom';
import BottomLink from '../../components/BottomLink';

function QuickenLoans() {
  return (
    <div className="bww-page page-work"><div className="bww-section section-case-studies">
    <div className="case-study">
      <h2>Quicken Loans
      <Link className="cs-nav cs-nav-prev" to="/case-leadgen">&#9650;</Link>
      <Link className="cs-nav cs-nav-next" to="/case-rnf">&#9660;</Link>
      </h2>
      <div className="bww-row">
        <img className="bww-col-23up bww-centered" src="./images/case-studies/ql-logo.svg" alt="Quicken Loans Logo" />
      </div>
      <div class="bww-legend">

      <p><b>Project:</b> quickenloans.com</p>
      <p><b>My role:</b> Engineering lead / technical owner (frontend/platform)</p>
      <p><b>Timeline:</b> Phase 1 = 3 months, Phase 2 = 3 months</p>
      <p><b>Team:</b> Led 12+ engineers, plus cross-functional partners: SEO, product, design, content, data analytics</p>
      </div>

      <h3>Context</h3>
      <p>QuickenLoans.com was a high-traffic loan-originator site (3M+ visits/month, ~1,000 pages) we needed to acquire from its previous owner quickly, and transition toward a new brand direction: a loan marketplace. The business urgency was real: get control of core pages fast, so rebrand messaging could be staged immediately, while protecting the site’s premium SEO value. The existing CMS environment had evolved unevenly over time, becoming idiosyncratic, cumbersome, and high-maintenance — creating delivery risk and operational drag.</p>

      <h3>Hard constraints:</h3>
      <ul>
        <li><b>Speed-to-acquisition:</b> core pages had to be acquired on a tight timeline to support a rebrand and new messaging.</li>
        <li><b>SEO preservation:</b> maintain rankings, index coverage, and link equity across ~1,000 pages during major structural change.</li>
        <li><b>Legacy instability:</b> disorganized CMS limited safe changes, and increased migration risk.</li>
        <li><b>Performance expectations:</b> improve or at least protect page performance while introducing a new architecture.</li>
      </ul>

      <h3>What I did: decisions + executions</h3>
      <p><b><i>Decision 1: Ship acquisition in two phases instead of attempting a single “big bang”</i></b></p>
      <p><b>Why:</b> The rebrand required fast control of core customer-facing pages, but preserving SEO across a 1,000-page ecosystem demanded careful planning and validation.</p>
<p><b>Tradeoff</b>: Required intentional interim architecture: Phase 1 had to work cleanly while still allowing Phase 2 integration without rewrites.</p>
<p><b>Execution</b>: Defined two milestones with separate acceptance criteria and cutover plans:</p>

<ul>
  <li><b>Phase 1</b>: Acquire and re-architect the core pages to enable rebrand messaging quickly, while linking to the SEO information blog for depth and continuity.</li>
  
  <li><b>Phase 2</b>: Bring the SEO information blog into the new architecture without losing SEO equity, and improve performance holistically.</li>
</ul>

<p><b><i>Decision 2: Treat SEO as a first-class migration requirement, not a post-launch concern</i></b></p>

<p><b>Why</b>: SEO value was premium, and losing it would permanently degrade acquisition economics.</p>
<p><b>Tradeoff</b>: More up-front rigor: URL mapping, redirects, metadata parity, templating consistency, validation cycles.</p>
<p><b>Execution</b>: Established an SEO safety checklist and migration governance:</p>

<ul>
  <li>Worked closely with SEO stakeholders.</li>
  <li>URL strategy and redirect mapping to preserve link equity and avoid crawl dead-ends.</li>
  <li>Canonicalization and metadata parity (titles, descriptions, headings, structured data where applicable).</li>
  <li>Pre-launch validation plan with SEO stakeholders and post-launch monitoring expectations.</li>
</ul>

<p><b><i>Decision 3: Lead with “governable architecture” so Phase 2 could plug in cleanly</i></b></p>

<p><b>Why</b>: The legacy CMS fragmentation was the root problem; we needed a foundation that could absorb the blog content later without increasing complexity.</p>
<p><b>Tradeoff</b>: Some engineering time went to platform/architecture work rather than only page build-out.</p>
<p><b>Execution</b>: Re-architected core page templates and shared components to support:</p>

<ul>
  <li>Consistent layout and navigation patterns across the rebranded core pages.</li>
  
  <li>A clear integration seam for the SEO blog content (so Phase 2 was additive, not a rewrite).</li>
  
  <li>Performance and operational standards (repeatable builds/deploys, stable publishing workflow, consistent analytics tagging).</li>
</ul>

<h3>Phase outcomes</h3>

<p><b><i>Phase 1 (3 months): Acquired core pages for rapid rebrand staging</i></b></p>

<ul>
  <li>Delivered a new architecture for core pages enabling immediate rollout of rebrand messaging.</li>
  
  <li>Preserved access to deeper SEO information by linking to the existing information blog during the interim.</li>
  
  <li>Established the technical foundation and governance required for a safe Phase 2 integration.</li>
</ul>

<p><b><i>Phase 2 (3 months): Integrate the SEO information blog into the new architecture</i></b></p>

<ul>
  <li>Incorporated the SEO information blog into the new platform while preserving SEO value.</li>
  
  <li>Improved performance across the expanded footprint, rather than accepting regressions as the cost of migration.</li>
  
  <li>Completed the acquisition into a unified architecture suitable for marketplace evolution.</li>
</ul>

<h3>Results</h3>

<ul>
  <li><b>Speed</b>: Core acquisition shipped in 6 months total (Phase 1 in 3 months, Phase 2 in 3 months).</li>
  
  <li><b>Scale protected</b>: Maintained SEO continuity across ~1,000 pages and 3M+ monthly visits.</li>
    
  <li><b>Operational wins</b>:
    <ul>
      <li><b>Monetized preserved organic traffic</b>: used existing demand to drive marketplace conversion via lead-generation CTAs.</li>

      <li><b>Unblocked publishing throughput</b>: Since CMS was a streamlined, purpose-fit publishing workflow: operational friction was cut so content velocity was primarily constrained by editorial capacity, not tooling.</li>
    </ul>
  </li> 
</ul>

      <p><b>Tech stack:</b> Decoupled architecture; React, JavaScript/TypeScript, Gatsby.js, WordPress, GraphQL, AWS S3/CloudFront/Lambda, CloudFlare, MariaDB, Storybook, testing libraries, yarn monorepo</p>

      <img aria-hidden="true" src="https://www.bushnellwebworks.com/anon2020.php?w=ql" style={{width:'1px',height:'1px',opacity:0}} />

    </div>
    <BottomLink to="/case-rnf" label="Nixon Foundation" />
    </div>
    </div>
  );
}

export default QuickenLoans;
